import React, { useEffect, useRef, useState } from 'react';
import FlexComponent from '../FlexComponent';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import { Avatar } from 'primereact/avatar';
import { ErrorMessage } from 'formik';

function AddressHistory({
  values,
  formName = "",
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  toast
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ newEntry: {}, addressHistory: [] });
  const inputRefs = useRef([]);
  const autoCompleteRefs = useRef([]);
  const [editingIndex, setEditingIndex] = useState(null);

  // Fixed: status is now always false for new entries after first address
  const [newAddress, setNewAddress] = useState({
    full_address: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    home_status: '',
    status: false, // ← Always false for new entries (unless it's the very first address)
    rent_amount: null,
    move_in_date: null,
    move_out_date: null,
  });

  // Sync status for newAddress when there are zero addresses
  useEffect(() => {
    const hasNoAddresses = !values.address_history || values.address_history.length === 0;
    if (hasNoAddresses) {
      setNewAddress(prev => ({ ...prev, status: true }));
    } else {
      setNewAddress(prev => ({ ...prev, status: false }));
    }
  }, [values.address_history?.length]);

  // console.log("values.address_history", values.address_history);
  
  useEffect(() => {
  if (window.google && window.google.maps) {

    // Clear stale autocomplete ref when entering edit mode so it reinitializes fresh
    if (editingIndex !== null) {
      autoCompleteRefs.current[editingIndex] = null;
    }

    // Autocomplete for existing addresses
    values.address_history?.forEach((_, index) => {
      if (!autoCompleteRefs.current[index] && inputRefs.current[index]) {
        if (!(inputRefs.current[index] instanceof HTMLInputElement)) return;
        autoCompleteRefs.current[index] =
          new window.google.maps.places.Autocomplete(
            inputRefs.current[index],
            {
              types: ['geocode'],
              componentRestrictions: { country: 'us' },
            }
          );

        autoCompleteRefs.current[index].addListener(
          'place_changed',
          () => {
            const place =
              autoCompleteRefs.current[index].getPlace();

            const updatedHistory = [...values.address_history];
            let streetNumber = '';
            let route = '';

            updatedHistory[index] = {
              ...updatedHistory[index],
              full_address: place.formatted_address || '',
              street_address: '',
              city: '',
              state: '',
              zip_code: '',
            };

            place.address_components?.forEach((component) => {
              const types = component.types;

              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              }

              if (types.includes('route')) {
                route = component.long_name;
              }

              // ✅ CITY FIX (Astoria support)
              if (
                types.includes('locality') ||
                types.includes('sublocality_level_1')
              ) {
                updatedHistory[index].city =
                  component.long_name;
              }

              if (
                types.includes(
                  'administrative_area_level_1'
                )
              ) {
                updatedHistory[index].state =
                  component.long_name;
              }

              if (types.includes('postal_code')) {
                updatedHistory[index].zip_code =
                  component.long_name;
              }
            });

            if (streetNumber || route) {
              updatedHistory[index].street_address =
                `${streetNumber} ${route}`.trim();
            }

            setFieldValue('address_history', updatedHistory);

            const validationErrors = validateAddress(
              updatedHistory[index],
              index === 0 || updatedHistory[index].status
            );

            setError((prev) => {
              const newErrors = [...prev.addressHistory];
              newErrors[index] = validationErrors;
              return {
                ...prev,
                addressHistory: newErrors,
              };
            });
          }
        );
      }
    });

    // Autocomplete for new address input
    if (
      !autoCompleteRefs.current['new'] &&
      inputRefs.current['new']
    ) {
      if (!(inputRefs.current['new'] instanceof HTMLInputElement)) return;
      autoCompleteRefs.current['new'] =
        new window.google.maps.places.Autocomplete(
          inputRefs.current['new'],
          {
            types: ['geocode'],
            componentRestrictions: { country: 'us' },
          }
        );

      autoCompleteRefs.current['new'].addListener(
        'place_changed',
        () => {
          const place =
            autoCompleteRefs.current['new'].getPlace();

          let streetNumber = '';
          let route = '';

          const updatedAddress = {
            ...newAddress,
            full_address: place.formatted_address || '',
            street_address: '',
            city: '',
            state: '',
            zip_code: '',
          };

          place.address_components?.forEach((component) => {
            const types = component.types;

            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            }

            if (types.includes('route')) {
              route = component.long_name;
            }

            // ✅ CITY FIX (Astoria support)
            if (
              types.includes('locality') ||
              types.includes('sublocality_level_1')
            ) {
              updatedAddress.city = component.long_name;
            }

            if (
              types.includes(
                'administrative_area_level_1'
              )
            ) {
              updatedAddress.state = component.long_name;
            }

            if (types.includes('postal_code')) {
              updatedAddress.zip_code =
                component.long_name;
            }
          });

          if (streetNumber || route) {
            updatedAddress.street_address =
              `${streetNumber} ${route}`.trim();
          }

          console.log("updatedAddress", updatedAddress);
          
          setNewAddress(updatedAddress);

          const validationErrors = validateAddress(
            updatedAddress,
            updatedAddress.status
          );

          setError((prev) => ({
            ...prev,
            newEntry: validationErrors,
          }));
        }
      );
    }
  }
}, [values.address_history, setFieldValue, editingIndex]);


  const calculateDuration = () => {
    if (!values.address_history || values.address_history.length === 0) return 0;
    let totalMonths = 0;
    values.address_history.forEach((address) => {
      if (address.move_in_date) {
        const start = new Date(address.move_in_date);
        const end = address.move_out_date ? new Date(address.move_out_date) : new Date();
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        totalMonths += months;
      }
    });
    return totalMonths / 12;
  };

  const validateAddress = (address, isCurrent) => {
    const errors = {};
    if (!address.full_address?.trim()) errors.full_address = true;
    if (!address.home_status) errors.home_status = true;
    if (!address.move_in_date) errors.move_in_date = true;
    if (!isCurrent && !address.move_out_date) errors.move_out_date = true;
    return errors;
  };

  const handleAddClick = () => {
    const isFirstAddress = !values.address_history || values.address_history.length === 0;
    const shouldBeCurrent = isFirstAddress; // Only first address can be current

    const validationErrors = validateAddress(newAddress, shouldBeCurrent);

    if (Object.keys(validationErrors).length > 0) {
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
      return;
    }

    setLoading(true);

    // Ensure only one address has status: true
    let updatedHistory = [...(values.address_history || [])];
    if (shouldBeCurrent) {
      updatedHistory = updatedHistory.map(addr => ({ ...addr, status: false }));
    }

    updatedHistory.push({
      ...newAddress,
      status: shouldBeCurrent
    });

    setFieldValue('address_history', updatedHistory);

    // Reset newAddress — status always false for next entry
    setNewAddress({
      full_address: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      home_status: '',
      status: false,
      rent_amount: null,
      move_in_date: null,
      move_out_date: null,
    });

    setError((prev) => ({ ...prev, newEntry: {} }));
    setTimeout(() => setLoading(false), 1000);
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[index] = newAddressHistoryErrors[index] || {};
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleSaveEdit = (index) => {
    const address = values.address_history[index];
    const isCurrent = index === 0 || address.status === true;
    const validationErrors = validateAddress(address, isCurrent);

    if (Object.keys(validationErrors).length > 0) {
      setError((prev) => {
        const newAddressHistoryErrors = [...prev.addressHistory];
        newAddressHistoryErrors[index] = validationErrors;
        return { ...prev, addressHistory: newAddressHistoryErrors };
      });
      return;
    }

    setEditingIndex(null);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[index] = {};
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleDeleteClick = (index) => {
    const updatedHistory = values.address_history.filter((_, idx) => idx !== index);
    setFieldValue('address_history', updatedHistory);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors.splice(index, 1);
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleInputChange = (index, field, value) => {
    const updatedHistory = [...values.address_history];
    updatedHistory[index] = { ...updatedHistory[index], [field]: value };
    setFieldValue('address_history', updatedHistory);

    const validationErrors = validateAddress(updatedHistory[index], index === 0 || updatedHistory[index].status);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[index] = validationErrors;
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleNewEntryChange = (field, value) => {
    setNewAddress((prev) => {
      const newState = { ...prev, [field]: value };
      const isFirst = !values.address_history || values.address_history.length === 0;
      const validationErrors = validateAddress(newState, isFirst);
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
      return newState;
    });
  };

  const changeAvatarColor = (homeStatus) => {
    if (!homeStatus) return '#8C8C8C';
    if (homeStatus === 'Rent') return '#F141D0';
    if (homeStatus === 'Own') return '#418BCC';
    if (homeStatus === 'Live Rent Free') return '#EB4243';
    return '#8C8C8C';
  };

  const avatarLabel = (homeStatus) => {
    if (!homeStatus) return '';
    if (homeStatus === 'Rent') return 'R';
    if (homeStatus === 'Own') return 'O';
    if (homeStatus === 'Live Rent Free') return 'RF';
    return '';
  };

  const isLessThanTwoYears = calculateDuration() < 2;

  return (
    <div style={{ width: '100%', padding: '0.5rem 0 0 5.8%' }} id="addressId">
      <div>
        <FlexComponent>
          <div>
            {formName !== "Home Owner App" && <p style={{ fontSize: '28px', fontWeight: 'bold', padding: '0.75rem', fontFamily: 'Barlow Semi Condensed' }}>
              {values.address_history?.length > 0 ? 'Where did you live before?' : 'Where do you live now?'}
            </p>}
            <p style={{ padding: '0.75rem' }}>Address History</p>
          </div>
        </FlexComponent>
        {isLessThanTwoYears && (
          <p style={{ color: 'blue', fontSize: '14px', padding: '.2rem 0.75rem' }}>
            Please provide at least 2 years of address history.
          </p>
        )}
      </div>

      {values.address_history?.length > 0 && (
        <div>
          {values.address_history.map((address, index) => (
            <div key={index} style={{ background: 'white', padding: '0 1rem', borderRadius: '10px', margin: '1rem 0' }}>
              {editingIndex !== index ? (
                <FlexComponent alignItems="start" justifyContent="start" gap="1rem" style={{ textAlign: 'start' }}>
                  <div>
                    <Avatar
                      style={{ width: '4.5rem', color: 'white', background: changeAvatarColor(address?.home_status) }}
                      label={avatarLabel(address?.home_status)}
                      size="xlarge"
                    />
                  </div>
                  <div style={{ width: '50%' }}>
                    <p>{address?.full_address || 'No Address'}</p>
                    <p>
                      {address?.city}, {address?.state} {address?.zip_code}
                    </p>
                    <p>{address?.home_status || 'No Home Status'}</p>
                    {address?.rent_amount && <p>Rent Amount: ${address?.rent_amount}</p>}
                    <p>Move In: {address?.move_in_date ? new Date(address.move_in_date).toLocaleDateString() : 'N/A'}</p>
                    {!address.status && address?.move_out_date && (
                      <p>Move Out: {new Date(address.move_out_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <Button type="button" onClick={() => handleEditClick(index)}>
                    Edit
                  </Button>
                  {index !== 0 && (
                    <Button type="button" onClick={() => handleDeleteClick(index)}>
                      Delete
                    </Button>
                  )}
                </FlexComponent>
              ) : (
                <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
                  <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start">
                    <div
                      className="inputContainerDiv"
                      style={{ width: '40%', border: error.addressHistory[index]?.full_address && '1px solid red', borderRadius: '5px' }}
                    >
                      <InputText
                        ref={(el) => (inputRefs.current[index] = el)}
                        style={{ width: '100%' }}
                        value={address.full_address || ''}
                        onChange={(e) => handleInputChange(index, 'full_address', e.target.value)}
                        onBlur={handleBlur}
                        placeholder={index === 0 ? 'Current Address' : `Previous Address ${index}`}
                      />
                    </div>
                    <div
                      className="inputContainerDiv"
                      style={{ width: '20%', border: error.addressHistory[index]?.home_status && '1px solid red', borderRadius: '5px' }}
                    >
                      <Dropdown
                        value={address.home_status || ''}
                        options={[
                          { label: 'Rent', value: 'Rent' },
                          { label: 'Own', value: 'Own' },
                          { label: 'Live Rent Free', value: 'Live Rent Free' },
                        ]}
                        style={{ width: '100%' }}
                        onChange={(e) => handleInputChange(index, 'home_status', e.value)}
                        onBlur={handleBlur}
                        placeholder="Address Type"
                      />
                    </div>
                    {address.home_status === 'Rent' && (
                      <div
                        className="inputContainerDiv"
                        style={{ width: '20%', border: error.addressHistory[index]?.rent_amount && '1px solid red', borderRadius: '5px' }}
                      >
                        <InputText
                          type="number"
                          style={{ width: '100%' }}
                          value={address.rent_amount || ''}
                          onChange={(e) => handleInputChange(index, 'rent_amount', e.target.value)}
                          onBlur={handleBlur}
                          placeholder="Rent Amount"
                        />
                      </div>
                    )}
                    <div
                      className="inputContainerDiv"
                      style={{ width: '20%', border: error.addressHistory[index]?.move_in_date && '1px solid red', borderRadius: '5px' }}
                    >
                      <div className="p-inputgroup flex-1 login-input-div">
                        <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                          <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                        </span>
                        <Calendar
                          value={address.move_in_date ? new Date(address.move_in_date) : null}
                          onChange={(e) => handleInputChange(index, 'move_in_date', e.value)}
                          style={{ width: '100%' }}
                          placeholder="Move In Date"
                          maxDate={new Date()}
                          className="inputWithIcon"
                        />
                      </div>
                    </div>
                    {index !== 0 && (
                      <div
                        className="inputContainerDiv"
                        style={{ width: '20%', border: error.addressHistory[index]?.move_out_date && '1px solid red', borderRadius: '5px' }}
                      >
                        <div className="p-inputgroup flex-1 login-input-div">
                          <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                            <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                          </span>
                          <Calendar
                            value={address.move_out_date ? new Date(address.move_out_date) : null}
                            onChange={(e) => handleInputChange(index, 'move_out_date', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Move Out Date"
                            maxDate={new Date()}
                            className="inputWithIcon"
                          />
                        </div>
                      </div>
                    )}
                  </FlexComponent>
                  <FlexComponent justifyContent="center" gap="1rem">
                    <Button type="button" onClick={() => handleSaveEdit(index)}>
                      Save
                    </Button>
                    <Button type="button" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </FlexComponent>
                </FlexComponent>
              )}
              <Divider />
            </div>
          ))}
        </div>
      )}

      <div style={{ background: 'white', padding: '1rem', borderRadius: '10px', margin: '1rem 0' }}>
        <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
          <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start">
            <div
              className="inputContainerDiv"
              style={{ width: '40%', border: error.newEntry.full_address && '1px solid red', borderRadius: '5px' }}
            >
              <InputText
                ref={(el) => (inputRefs.current['new'] = el)}
                style={{ width: '100%' }}
                value={newAddress.full_address || ''}
                onChange={(e) => handleNewEntryChange('full_address', e.target.value)}
                onBlur={handleBlur}
                placeholder={values.address_history?.length > 0 ? 'Previous Address' : 'Current Address'}
              />
            </div>
            <div
              className="inputContainerDiv"
              style={{ width: '20%', border: error.newEntry.home_status && '1px solid red', borderRadius: '5px' }}
            >
              <Dropdown
                value={newAddress.home_status || ''}
                options={[
                  { label: 'Rent', value: 'Rent' },
                  { label: 'Own', value: 'Own' },
                  { label: 'Live Rent Free', value: 'Live Rent Free' },
                ]}
                style={{ width: '100%' }}
                onChange={(e) => handleNewEntryChange('home_status', e.value)}
                onBlur={handleBlur}
                placeholder="Address Type"
              />
            </div>
            {newAddress.home_status === 'Rent' && (
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.rent_amount && '1px solid red', borderRadius: '5px' }}
              >
                <InputText
                  type="number"
                  style={{ width: '100%' }}
                  value={newAddress.rent_amount || ''}
                  onChange={(e) => handleNewEntryChange('rent_amount', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Rent Amount"
                />
              </div>
            )}
            <div
              className="inputContainerDiv"
              style={{ width: '20%', border: error.newEntry.move_in_date && '1px solid red', borderRadius: '5px' }}
            >
              <div className="p-inputgroup flex-1 login-input-div">
                <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                  <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                </span>
                <Calendar
                  value={newAddress.move_in_date ? new Date(newAddress.move_in_date) : null}
                  onChange={(e) => handleNewEntryChange('move_in_date', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Move In Date"
                  maxDate={new Date()}
                  className="inputWithIcon"
                />
              </div>
            </div>
            {newAddress.status === false && values.address_history?.length > 0 && (
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.move_out_date && '1px solid red', borderRadius: '5px' }}
              >
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                  </span>
                  <Calendar
                    value={newAddress.move_out_date ? new Date(newAddress.move_out_date) : null}
                    onChange={(e) => handleNewEntryChange('move_out_date', e.value)}
                    style={{ width: '100%' }}
                    placeholder="Move Out Date"
                    maxDate={new Date()}
                    className="inputWithIcon"
                  />
                </div>
              </div>
            )}
          </FlexComponent>
          <FlexComponent justifyContent="center" gap="1rem">
            <Button
              type="button"
              label={values.address_history?.length === 0 ? 'Save Current Address' : 'Save Previous Address'}
              style={{ borderRadius: '5px', fontSize: '16px' }}
              onClick={handleAddClick}
              disabled={loading}
            />
          </FlexComponent>
        </FlexComponent>
        <div style={{ textAlign: 'center', marginTop: '1rem', width: '100%' }}>
          <ErrorMessage name="address_history" component="div" className="error" style={{ color: 'red' }} />
        </div>
      </div>

      <Divider />
    </div>
  );
}

export default AddressHistory;